import React from 'react';
import { Layout, Typography, Grid, Progress, Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import LanguageSwitcher from '../../../components/auth/LanguageSwitcher';
import { appVersion } from '../../../config/config';
import { signupPalette, signupGradient } from './signupTheme';

const { Title, Text } = Typography;
const { Content, Footer } = Layout;

type Props = {
    /** Titre de l'écran courant — une seule question par écran. */
    title: string;
    subtitle?: string;
    /** Numéro de l'étape courante (1-based), absent en freemium. */
    step?: number;
    totalSteps?: number;
    /** Libellé de l'étape affiché au-dessus de la barre de progression. */
    stepLabel?: string;
    onBack?: () => void;
    children: React.ReactNode;
};

/**
 * Coquille visuelle du tunnel : reprend l'identité de la page de connexion et
 * n'affiche qu'une question à la fois, avec une progression toujours visible —
 * c'est ce qui évite à l'utilisateur de se perdre en chemin.
 */
const SignupLayout: React.FC<Props> = ({ title, subtitle, step, totalSteps, stepLabel, onBack, children }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const screens = Grid.useBreakpoint();
    const isCompact = screens.md !== true;

    const showProgress = typeof step === 'number' && typeof totalSteps === 'number' && totalSteps > 1;

    return (
        <Layout
            style={{
                minHeight: '100vh',
                position: 'relative',
                background: signupPalette.dark,
                padding: isCompact
                    ? 'max(12px, env(safe-area-inset-top)) max(12px, env(safe-area-inset-right)) 24px max(12px, env(safe-area-inset-left))'
                    : '40px',
                paddingTop: isCompact ? 'max(52px, calc(env(safe-area-inset-top) + 40px))' : undefined,
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    top: isCompact ? 'max(12px, env(safe-area-inset-top))' : 40,
                    right: isCompact ? 'max(12px, env(safe-area-inset-right))' : 40,
                    zIndex: 1000,
                }}
            >
                <LanguageSwitcher variant="dark" />
            </div>

            <Content
                style={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: isCompact ? 'flex-start' : 'center',
                    maxWidth: 1200,
                    width: '100%',
                    margin: '0 auto',
                }}
            >
                <div
                    style={{
                        width: '100%',
                        maxWidth: 880,
                        backgroundColor: signupPalette.surface,
                        borderRadius: isCompact ? 16 : 24,
                        overflow: 'hidden',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                >
                    {/* Bandeau : logo + progression */}
                    <div
                        style={{
                            background: signupGradient,
                            padding: isCompact ? '20px 20px 24px' : '28px 40px 32px',
                            color: signupPalette.surface,
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                            <img
                                src="/images/logo_120x64.png"
                                alt="Tilmicode"
                                style={{ height: isCompact ? 40 : 52, filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.2))' }}
                            />
                            {showProgress && (
                                <Text style={{ color: signupPalette.surface, fontWeight: 600, fontSize: isCompact ? 13 : 14 }}>
                                    {t('signup.progress.stepOf', 'Étape {{current}} sur {{total}}', {
                                        current: step,
                                        total: totalSteps,
                                    })}
                                </Text>
                            )}
                        </div>

                        {showProgress && (
                            <div style={{ marginTop: 14 }}>
                                <Progress
                                    percent={Math.round(((step as number) / (totalSteps as number)) * 100)}
                                    showInfo={false}
                                    strokeColor={signupPalette.surface}
                                    trailColor="rgba(255,255,255,0.35)"
                                    strokeWidth={6}
                                />
                                {stepLabel && (
                                    <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13 }}>{stepLabel}</Text>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Contenu de l'étape */}
                    <div style={{ padding: isCompact ? '24px 20px 28px' : '40px 56px 44px' }}>
                        {onBack && (
                            <Button
                                type="text"
                                icon={<ArrowLeftOutlined />}
                                onClick={onBack}
                                style={{ marginBottom: 8, paddingLeft: 0, color: signupPalette.textSecondary }}
                            >
                                {t('signup.actions.back', 'Retour')}
                            </Button>
                        )}

                        <Title
                            level={2}
                            style={{
                                margin: 0,
                                color: signupPalette.textPrimary,
                                fontSize: isCompact ? 22 : 28,
                                fontWeight: 700,
                                lineHeight: 1.25,
                            }}
                        >
                            {title}
                        </Title>
                        {subtitle && (
                            <Text style={{ color: signupPalette.textSecondary, fontSize: 15, display: 'block', marginTop: 8 }}>
                                {subtitle}
                            </Text>
                        )}

                        <div style={{ marginTop: isCompact ? 24 : 32 }}>{children}</div>
                    </div>

                    {/* Lien de connexion */}
                    <div
                        style={{
                            textAlign: 'center',
                            padding: '18px 24px 24px',
                            borderTop: `1px solid ${signupPalette.border}`,
                        }}
                    >
                        <Text style={{ color: signupPalette.textSecondary, fontSize: 14 }}>
                            {t('signup.alreadyHaveAccount', 'Vous avez déjà un compte ?')}{' '}
                            <a
                                href="/login"
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate('/login');
                                }}
                                style={{ color: signupPalette.primary, fontWeight: 600, textDecoration: 'none' }}
                            >
                                {t('signup.actions.login', 'Se connecter')}
                            </a>
                        </Text>
                    </div>
                </div>
            </Content>

            <Footer style={{ background: 'transparent', padding: isCompact ? '16px 12px' : 24, textAlign: 'center' }}>
                <Text style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: isCompact ? 12 : 13 }}>
                    {t('auth.login.footer', { year: new Date().getFullYear(), version: appVersion || 'dev' })}
                </Text>
            </Footer>
        </Layout>
    );
};

export default SignupLayout;
